import { AxInit, ModuleName } from "../../Lib/AxSystem/AxInit";
import { AxDatePicker, Cell, Mode } from "../../Components/AxDatepicker/AxDatepicker";
import { AxDropdown, AxInput } from "@axess/limestone";
import tippy, { Instance, Props } from 'tippy.js';
import { AxCounter } from "../../Limestone/AxCounter/AxCounter";
import { PriceHelper } from "../../Lib/Helper/PriceHelper";
import { Subject } from "rxjs";
import { RequestHelper } from "../../Lib/Helper/RequestHelper";
import { LessonIdentifier } from "../../Models/Lesson/LessonIdentifier";
import { prod } from "mathjs";
import { I18nHelper, Translations } from "../../Lib/Helper/I18nHelper";

interface ILessonsDetailsInitData {
    startOfWeek: number;
    startDate: string;
    endDate?: string;
    datePattern: string;
    lang: string;
    identifier: LessonIdentifier;
    allowedDates: string[];
    products: ILessonProductExtended[];
    submitUrl: string
}

class ILessonProduct {
    subTypeIdentifier: number;
    quantity: number;
    projNr: number;
    productGroupIdentifier: number;
    id: number;
    type: number;
    discounts: LessonDiscount[]
}

class LessonDiscount {
    tariff: number;
    position: number;
}

class ILessonProductExtended extends ILessonProduct {
    tariff: number;
    counter: AxCounter;
    subTypeName: string;
}

interface ILessonSelectionDto {
    identifier: LessonIdentifier,
    lessonNr: number;
    date: string;
    products: ILessonProduct[],
}

const toLessonProduct = (lp: ILessonProductExtended): ILessonProduct => {
    return {
        subTypeIdentifier: lp.subTypeIdentifier,
        quantity: lp.quantity,
        projNr: lp.projNr,
        productGroupIdentifier: lp.productGroupIdentifier,
        id: lp.id,
        type: lp.type,
        discounts: lp.discounts
    }
}

@ModuleName("LessonsDetails")
export class LessonsDetails implements AxInit {
    public initData: ILessonsDetailsInitData;
    public datePicker: AxDatePicker;

    public _dateDropDown: AxDropdown = new AxDropdown();
    public _quantityDropdown: AxDropdown = new AxDropdown();
    public _quantitySelectionTippy: Instance<Props>;

    public counter: AxCounter[] = [];
    public selectedSlot?: number = null;

    public inputEvent: Subject<boolean> = new Subject<boolean>();
    public availableSlots: any[];

    constructor(private _priceHelper = new PriceHelper(null, true),
        private _requestHelper = new RequestHelper(),
        private _i18nHelper = new I18nHelper()) {

    }

    AxInit(init?: ILessonsDetailsInitData): void {
        this.initData = init;
        this.datePicker = new AxDatePicker();

        this.datePicker.Init({
            StartOfWeek: init.startOfWeek,
            Lang: init.lang,
            StartDate: moment(init.startDate, "YYYY-MM-DD"),
            DayRange: 0,
            Target: '[data-lesson="datepicker"]',
            Mode: Mode.Inline,
            EndDate: null,
            monthsShown: 1,
            BlockedDates: [],
            AvailableDates: init.allowedDates.map(s => moment(s, "YYYY-MM-DD", true))
        });

        this.datePicker.ActivationSubject.subscribe(x => { this.setDate(x); });

        $('[data-lesson="apply-quantity"]').on('click', (e) => {
            let text = [];

            for (const prod of this.initData.products) {
                const quantity = prod.counter.GetValue();

                prod.quantity = quantity;

                if (quantity > 0) {
                    text.push(`${prod.quantity} ${prod.subTypeName}`);
                }
            }
            this._quantitySelectionTippy.hide();

            if (text.length > 0) {
                this._quantityDropdown.setValue(text.join(', '));
            } else {
                this._quantityDropdown.resetSelection();
            }
            this.inputEvent.next(true);
        });

        $('[data-lesson="slot-container"]').on('click', '[data-slot-starttime]', (e) => {
            const $button = $(e.currentTarget);

            if ($button.attr('data-active') == "false") return;

            this.checkSlot($button);
            this.inputEvent.next(true);
        });

        this.inputEvent.subscribe(() => {
            const prodsWithQuantity = this.initData.products.filter(x => typeof x.quantity !== 'undefined' && x.quantity !== 0);

            let priceTotal = 0;
            let totalQuantity = 0;

            for (let prod of prodsWithQuantity) {
                priceTotal += this.getProductPrice(prod);
                totalQuantity += prod.quantity;
            }
            this.updateSlotColor(totalQuantity);

            const allSelected = (prodsWithQuantity.length !== 0 && this.selectedSlot != null);

            $('[data-lesson="subtotal"]').html(this._priceHelper.GetPriceString(priceTotal / 100));
            $('[data-lesson="submit-wrapper"]').toggleClass('d-none', !allSelected).toggleClass('d-flex', allSelected);
        });

        this._dateDropDown.init(`[data-lesson="datepicker-dropdown"]`,
            {
                searchBar: false,
                multiSelect: false
            });

        this._quantityDropdown.init(`[data-lesson="quantity-dropdown"]`,
            {
                searchBar: false,
                multiSelect: false
            });

        this._quantitySelectionTippy = tippy(document.querySelector('[data-lesson="quantity-wrapper"]'), {
            allowHTML: true,
            interactive: true,
            content: document.querySelector('[data-lesson="quantity"]'),
            placement: 'bottom-start',
            arrow: false,
            trigger: 'manual',
            maxWidth: 'none',
            appendTo: () => document.body,
            onShow(instance) {
                instance.popper.style.width = $('[data-lesson="selection"]')
                    ?.get(0)
                    .getBoundingClientRect().width / 100 * 60 + 'px';
                $(window).on('scroll', () => instance.hide());
            },
        });

        $('[data-lesson="quantity-dropdown"]').on('click', (e) => {
            if (!$(e.delegateTarget).attr('disabled')) {
                this._quantitySelectionTippy.show();
            }
        });

        $('[data-lesson="submit"]').on('click', (e) => {
            const productData = this.initData.products.map((e) => {
                return toLessonProduct(e)
            });

            const dataToSend: ILessonSelectionDto = {
                products: productData,
                identifier: this.initData.identifier,
                date: this.datePicker.ActivationSubject.getValue().Date.format('YYYY-MM-DD'),
                lessonNr: this.selectedSlot,
            };

            const queryString = this._requestHelper.base64UrlSave(dataToSend);
            window.location.href = `${this.initData.submitUrl}?r=${queryString}`;
        });

        $('[data-lesson="slot-container"]').on('click', '[data-lesson="view-schedule-btn"]', (e) => {
            $('#lesson-schedule').modal('show');
        });

        const $popper = $(this._quantitySelectionTippy.popper);
        const $lines = $popper.find(`.prod_selection__prod-list`);

        for (let elem of $lines) {
            this.initCounter(elem);
        }
    }


    initCounter(elem: HTMLElement) {
        const $elem = $(elem);
        const quantity = parseInt($elem.find('[data-quantity-selection]').html(), 10);
        const subTypeIdentifier = parseInt($(elem).attr('data-subtypeidentifier'));
        const product = this.initData.products.find(x => x.subTypeIdentifier === subTypeIdentifier);

        const axCounter = new AxCounter();
        axCounter.Init({
            Value: quantity,
            Selector: elem,
            Max: parseInt($elem.attr('data-max') ?? "10"),
            Min: parseInt($elem.attr('data-min') ?? "0"),
        });
        axCounter.Subscribe((x) => {
            const subTypeIdentifier = parseInt($(elem).attr('data-subtypeidentifier'), 10);
            const price = this.getProductPrice(product, x);

            $(`[data-calculated-price=${subTypeIdentifier}]`).html(this._priceHelper.GetPriceString(price / 100));

            if (product.discounts != null && product.discounts.length > 0) {
                $(`[data-calculated-price-next=${subTypeIdentifier}]`)
                    .removeClass('d-none')
                    .html(`${this._i18nHelper.GetTranslation(Translations.NextAdditionalPerson)} ${this._priceHelper.GetPriceStringWithCurrencySymbol(this.getPrice(product, x + 1) / 100)}`);
            } else {
                $(`[data-calculated-price-next=${subTypeIdentifier}]`).addClass('d-none');
            }
        });

        product.counter = axCounter;
    }

    getProductPrice(product: ILessonProductExtended, quantity?: number) {
        let price = 0;
        quantity ??= product.quantity;

        for (let i = 1; i <= quantity; i++) {
            price += this.getPrice(product, i);
        }
        price = quantity == 0 ? product.tariff : price;

        return price;
    }

    getPrice(product: ILessonProductExtended, index: number) {
        const maxDiscount = product.discounts?.reduce(
            (max, comp) => (comp.position > max.position ? comp : max),
            product.discounts[0]
        );

        if (maxDiscount == null) {
            return product.tariff;
        }

        if (index >= maxDiscount.position) {
            return maxDiscount.tariff;
        }

        let nextDiscount = product.discounts.find(y => y.position == index);

        if (nextDiscount != null) {
            return nextDiscount.tariff;
        }

        return product.tariff;
    }

    setDate(cell: Cell) {
        if (cell != null) {
            $('[data-lesson="slot-container"]').html('').addClass('d-none');
            $('[data-lesson="skeleton"]').addClass('d-flex').removeClass('d-none');
            this._dateDropDown.setValue(cell.Date.format(this.initData.datePattern));
            this._dateDropDown.close();

            AxessViewer.loadPartialView(`/Products/Lessons/_GetSlots`, {
                date: cell.Date.format('YYYY-MM-DD'),
                i: this.initData.identifier
            }, (resp) => {

                this.availableSlots = resp.slots;
                this.selectedSlot = null;
                $('[data-lesson="slot-container"]').html(resp.returnView).removeClass('d-none');
                $('[data-lesson="skeleton"]').removeClass('d-flex').addClass('d-none');


                let totalQuantity = 0;
                const prodsWithQuantity = this.initData.products.filter(x => typeof x.quantity !== 'undefined' && x.quantity !== 0);

                for (let prod of prodsWithQuantity) {
                    totalQuantity += prod.quantity;
                }

                this.updateSlotColor(totalQuantity);
            })
        }
    }

    checkSlot($button: JQuery<HTMLElement>) {
        const startTime = $button.attr('data-slot-starttime');

        this.selectedSlot = parseInt($button.attr('data-lessonnr'));

        const slot = this.availableSlots?.find(x => x.lessonNr == this.selectedSlot);

        if (slot.programSlots != null && slot.programSlots.length > 0) {
            let dates: string[] = [];

            $('#lesson-schedule-content > div:not([data-grid-header])').remove();
            console.log(slot.programSlots);
            for (let programSlot of slot.programSlots) {
                const from = moment(programSlot.dtFrom, "YYYY-MM-DD HH:mm:ss");
                dates.push(from.format('L LT'));

                const dur = moment.duration(programSlot.lessonDurationNr, 'minutes');
                $('#lesson-schedule-content').append($('<div></div>').addClass("ax-border-top ax-py-1 ax-px-2 ax-font-weight-medium").html(from.format('L')));
                $('#lesson-schedule-content').append($('<div></div>').addClass("ax-border-top ax-py-1 ax-px-2").html(from.format('LT')));
                $('#lesson-schedule-content').append($('<div></div>').addClass("ax-border-top ax-py-1 ax-px-2").html(dur.humanize()));
            }
            $('[data-lesson="program-container"]').addClass('d-flex').removeClass('d-none');
            $('[data-lesson="program-list"]').html(dates.join(', '));
            $('[data-lesson="view-schedule"]').addClass('d-md-flex').removeClass('d-none');
        }

        for (let slot of $('[data-lesson="slot-container"] [data-slot-starttime]')) {
            const $tmpButton: JQuery<Element> = $(slot);
            const oldStatus: string = $tmpButton.attr('data-contingentstatus');

            $tmpButton.removeClass(`ax-fill-${oldStatus}-500`);
            $tmpButton.removeClass(`ax-outline-${oldStatus}-500`);

            let newStatus: string = oldStatus;

            newStatus = oldStatus;

            $tmpButton.removeClass('line-through');
            if ($tmpButton.is($button)) {
                $tmpButton.addClass(`ax-fill-${newStatus}-500`);
            } else {
                $tmpButton.addClass(`ax-outline-${newStatus}-500`);
            }
        }
    }

    uncheckSlot($slot: JQuery<HTMLElement>, mode: string, activeSlot: boolean) {
        $slot
            .removeClass(`ax-${mode}-orange-500`)
            .removeClass(`ax-${mode}-red-500`)
            .removeClass(`ax-${mode}-green-500`);

        if (activeSlot) {
            this.selectedSlot = null;
            $('[data-lesson="program-container"]').addClass('d-none').removeClass('d-flex');
            $('[data-lesson="view-schedule"]').addClass('d-none').removeClass('d-flex');
            $('[data-lesson="program-list"]').html('');
            this.inputEvent.next(null);
        }

        $slot.attr(`data-active`, 'false');
    }

    updateSlotColor(totalQ: number) {
        for (let slot of $('[data-lesson="slot-container"] [data-slot-starttime]')) {
            const $slot = $(slot);

            const free = parseInt($slot.attr('data-free'));
            const max = parseInt($slot.attr('data-max'));

            const threshold = Math.floor(max / 2);
            const freeAfterSelection = free - totalQ;

            $slot.removeAttr('data-active');
            const lessonNr = parseInt($slot.attr('data-lessonnr'));
            const activeSlot = this.selectedSlot == lessonNr;

            const mode = activeSlot ? "fill" : "outline";

            if (freeAfterSelection < 0) {
                this.uncheckSlot($slot, mode, activeSlot);
            } else if (freeAfterSelection <= 1) {
                $slot.attr(`data-contingentstatus`, `red`);
                $slot
                    .removeClass(`ax-fill-green-500`)
                    .removeClass(`ax-outline-green-500`)
                    .removeClass(`ax-fill-orange-500`)
                    .removeClass(`ax-outline-orange-500`)
                    .addClass(`ax-${mode}-red-500`);
            } else if (freeAfterSelection < threshold) {
                $slot.attr(`data-contingentstatus`, `orange`);
                $slot
                    .removeClass(`ax-fill-green-500`)
                    .removeClass(`ax-outline-green-500`)
                    .removeClass(`ax-fill-red-500`)
                    .removeClass(`ax-outline-red-500`)
                    .addClass(`ax-${mode}-orange-500`);
            } else {
                $slot.attr(`data-contingentstatus`, `orange`);
                $slot
                    .removeClass(`ax-fill-orange-500`)
                    .removeClass(`ax-outline-orange-500`)
                    .removeClass(`ax-fill-red-500`)
                    .removeClass(`ax-outline-red-500`)
                    .addClass(`ax-${mode}-green-500`);
            }
        }
    }
}